import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Lightbulb,
  Target,
  Loader2,
  ShieldCheck,
  RefreshCw,
} from "lucide-react";

const TOOL_LABELS = {
  pdsa: "PDSA",
  a3: "A3",
  fishbone: "Fishbone",
  "5whys": "5 Whys",
  sbar: "SBAR",
  rounding: "Safety Rounding",
  policy: "Policy Review",
};

const TOOL_COLORS = {
  pdsa: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  a3: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  fishbone: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  "5whys": "bg-amber-500/15 text-amber-300 border-amber-500/40",
  sbar: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  rounding: "bg-pink-500/15 text-pink-300 border-pink-500/40",
  policy: "bg-orange-500/15 text-orange-300 border-orange-500/40",
};

export default function IncidentTrendAnalysis() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  const run = async (windowDays = days) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/analytics/incident-trends`,
        { days: windowDays, comparison_days: windowDays },
        { withCredentials: true }
      );
      setData(res.data);
      setDays(windowDays);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not run trend analysis");
    } finally {
      setLoading(false);
    }
  };

  const trendIcon = (t) => {
    if (t === "up") return <TrendingUp className="w-5 h-5 text-red-400" />;
    if (t === "down") return <TrendingDown className="w-5 h-5 text-emerald-400" />;
    return <Minus className="w-5 h-5 text-zinc-400" />;
  };

  const trendChip = (t) => {
    const map = {
      up: "bg-red-500/15 text-red-300 border-red-500/40",
      down: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
      stable: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
    };
    return (
      <Badge variant="outline" className={`${map[t] || map.stable} capitalize`} data-testid="trend-overall-badge">
        {trendIcon(t)} <span className="ml-1">{t}</span>
      </Badge>
    );
  };

  return (
    <Card className="bento-card border-purple-500/30" data-testid="ai-trend-analysis-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between gap-3 flex-wrap">
          <span className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            AI Incident Trend Analysis
          </span>
          <div className="flex items-center gap-2">
            {[7, 14, 30].map((d) => (
              <Button
                key={d}
                size="sm"
                variant={days === d ? "default" : "outline"}
                onClick={() => run(d)}
                disabled={loading}
                className={days === d
                  ? "bg-purple-600 hover:bg-purple-500 text-white text-xs h-7"
                  : "bg-zinc-900 border-zinc-700 text-xs h-7"
                }
                data-testid={`trend-window-${d}`}
              >
                {d}d
              </Button>
            ))}
            <Button
              size="sm"
              onClick={() => run(days)}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-500 text-white text-xs h-7"
              data-testid="trend-run-btn"
            >
              {loading ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Analyzing...</>
              ) : data ? (
                <><RefreshCw className="w-3.5 h-3.5 mr-1" />Re-analyze</>
              ) : (
                <><Sparkles className="w-3.5 h-3.5 mr-1" />Generate analysis</>
              )}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-5">
        {!data && !loading && (
          <div className="py-8 text-center text-muted-foreground text-sm">
            Click <span className="text-purple-300 font-semibold">Generate analysis</span> to have Niko review your last {days} days of incidents and surface patterns, hypotheses, and recommended QI tools.
          </div>
        )}

        {loading && !data && (
          <div className="py-8 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Crunching the numbers...
          </div>
        )}

        {data && (
          <>
            {/* Top row — summary */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">Overall Trend</span>
                  {trendChip(data.overall_trend)}
                </div>
                <p className="text-sm text-zinc-200 leading-relaxed" data-testid="trend-narrative">
                  {data.narrative || "No narrative returned."}
                </p>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white" data-testid="trend-current-total">{data.stats?.current_total ?? 0}</div>
                  <div className="text-xs text-muted-foreground">current {data.stats?.window_days}d</div>
                </div>
                <div className="text-muted-foreground">vs</div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-zinc-400" data-testid="trend-prior-total">{data.stats?.prior_total ?? 0}</div>
                  <div className="text-xs text-muted-foreground">prior {data.stats?.comparison_days}d</div>
                </div>
              </div>
            </div>

            {/* Key Patterns */}
            {data.key_patterns?.length > 0 && (
              <section data-testid="trend-key-patterns">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-purple-300 font-semibold mb-2">
                  <Target className="w-3.5 h-3.5" /> Key Patterns
                </div>
                <ul className="space-y-1.5">
                  {data.key_patterns.map((p, i) => (
                    <li key={`pat-${i}`} className="text-sm text-zinc-200 flex items-start gap-2">
                      <span className="text-purple-400 mt-1">•</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Emerging Concerns */}
            {data.emerging_concerns?.length > 0 && (
              <section data-testid="trend-emerging-concerns">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-amber-300 font-semibold mb-2">
                  <AlertTriangle className="w-3.5 h-3.5" /> Emerging Concerns
                </div>
                <ul className="space-y-1.5">
                  {data.emerging_concerns.map((c, i) => (
                    <li key={`con-${i}`} className="text-sm text-zinc-200 flex items-start gap-2 bg-amber-500/5 border border-amber-500/20 rounded px-2.5 py-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Hypotheses */}
            {data.root_cause_hypotheses?.length > 0 && (
              <section data-testid="trend-hypotheses">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-blue-300 font-semibold mb-2">
                  <Lightbulb className="w-3.5 h-3.5" /> Root-Cause Hypotheses
                </div>
                <ul className="space-y-1.5">
                  {data.root_cause_hypotheses.map((h, i) => (
                    <li key={`hyp-${i}`} className="text-sm text-zinc-200 flex items-start gap-2">
                      <span className="text-blue-400 mt-1">?</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Recommended Actions */}
            {data.recommended_actions?.length > 0 && (
              <section data-testid="trend-recommended-actions">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-emerald-300 font-semibold mb-2">
                  <Sparkles className="w-3.5 h-3.5" /> Recommended Actions
                </div>
                <div className="space-y-2">
                  {data.recommended_actions.map((a, i) => (
                    <div key={`act-${i}`} className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
                      <div className="flex items-start gap-2 flex-wrap mb-1">
                        <Badge variant="outline" className={`text-xs uppercase ${TOOL_COLORS[a.tool] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/40"}`}>
                          {TOOL_LABELS[a.tool] || a.tool}
                        </Badge>
                        <span className="text-sm font-semibold text-white">{a.focus}</span>
                      </div>
                      <div className="text-xs text-zinc-400 leading-relaxed">{a.rationale}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Footer */}
            <div className="flex items-center gap-2 pt-3 border-t border-zinc-800 text-xs text-zinc-500">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>
                Only aggregate counts are sent to the LLM — incident narratives and notes stay in your private database.
              </span>
              {data.generated_at && (
                <span className="ml-auto">
                  Generated {new Date(data.generated_at).toLocaleTimeString()}
                </span>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
